import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useNavigate, useLocation } from 'react-router-dom'
import { alpha, styled } from '@mui/material/styles'
import Security from '@mui/icons-material/Security'
import Assignment from '@mui/icons-material/Assignment'
import ArrowBack from '@mui/icons-material/ArrowBack'
import Business from '@mui/icons-material/Business'
import Edit from '@mui/icons-material/Edit'
import FactCheck from '@mui/icons-material/FactCheck'
import LocationOn from '@mui/icons-material/LocationOn'
import ManageAccounts from '@mui/icons-material/ManageAccounts'
import MeetingRoom from '@mui/icons-material/MeetingRoom'
import OpenInNew from '@mui/icons-material/OpenInNew'
import Person from '@mui/icons-material/Person'
import Refresh from '@mui/icons-material/Refresh'
import Save from '@mui/icons-material/Save'
import Close from '@mui/icons-material/Close'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import {
  getPractitionerAuditBootstrap,
  getPractitionerAuditDetail,
  getPractitionerAuditRooms,
  savePractitionerRoomAuditors,
} from 'src/supportPortal/services/practitionerOnboardingService'
import { getUserRoles } from 'src/mentor/layout/utils'
import { toast } from 'react-toastify'
import AuditRequestsListing from './AuditRequestsListing'

const ROLE_AUDIT_ADMIN = 'ROLE_AUDIT_ADMIN'
const ROLE_THERAPIST_AUDIT_ADMIN = 'ROLE_THERAPIST_AUDIT_ADMIN'
const ROLE_SCREENER_AUDIT_ADMIN = 'ROLE_SCREENER_AUDIT_ADMIN'
const ROLE_ADMIN = 'ROLE_ADMIN'
const ROLE_THERAPIST = 'ROLE_THERAPIST'
const ROLE_SCREENER = 'ROLE_SCREENER'
const EVENT_TYPE_THERAPY_SESSION = 'therapy-session'
const EVENT_TYPE_SCREENING = 'screening'
const AUDITOR_TYPE_OPTIONS = [
  { id: 'SUPERVISOR', label: 'Supervisor' },
  { id: 'LEARNER', label: 'Learner' },
]

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  height: '100%',
  cursor: 'pointer',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
  borderRadius: theme.spacing(1),
  boxShadow: selected ? '0 8px 22px rgba(37, 99, 235, 0.14)' : '0 2px 8px rgba(0,0,0,0.06)',
  border: `1px solid ${selected ? alpha(theme.palette.primary.main, 0.45) : alpha(theme.palette.divider, 0.9)}`,
  background: selected ? alpha(theme.palette.primary.main, 0.04) : theme.palette.background.paper,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
    borderColor: alpha(theme.palette.primary.main, 0.4),
  },
}))

const TileIconContainer = styled(Box)(({ theme, color = 'primary.main' }) => ({
  width: 44,
  height: 44,
  borderRadius: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha(color === 'secondary' ? theme.palette.secondary.main : theme.palette.primary.main, 0.1),
  color: color === 'secondary' ? theme.palette.secondary.main : theme.palette.primary.main,
}))

const SectionPaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
  boxShadow: '0 2px 10px rgba(15, 23, 42, 0.05)',
}))

const EmptyPanel = ({ icon, title, body, action }) => (
  <Box sx={{ py: 6, px: 2, textAlign: 'center' }}>
    <Box sx={{ mb: 1.5, color: 'text.disabled' }}>{icon}</Box>
    <Typography variant="subtitle1" sx={{ fontWeight: 700 }} gutterBottom>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto' }}>
      {body}
    </Typography>
    {action && <Box sx={{ mt: 2 }}>{action}</Box>}
  </Box>
)

function trimValue(value) {
  return typeof value === 'string' ? value.trim() : ''
}

function normalizeId(value) {
  return trimValue(value)
}

function normalizeList(values = []) {
  if (typeof values === 'string') {
    return values.split(',').map(normalizeId).filter(Boolean)
  }
  return Array.isArray(values) ? values.map(normalizeId).filter(Boolean) : []
}

function displayTitle(entity, fallback = 'Unknown') {
  return (
    entity?.title ||
    entity?.name ||
    entity?.displayName ||
    entity?.fullName ||
    entity?.id ||
    fallback
  )
}

function getPractitionerType(detail, practitioner) {
  return trimValue(detail?.practitionerType || practitioner?.subtitle || practitioner?.type || 'therapist')
}

function getPractitionerRoleIds(detail, practitioner) {
  const serviceRoles = Object.keys(
    practitioner?.teacherUnEditableFields?.servicesApproved ||
      detail?.practitioner?.teacherUnEditableFields?.servicesApproved ||
      {},
  )
  const authorityRoles = [
    ...(Array.isArray(practitioner?.authorities) ? practitioner.authorities : []),
    ...(Array.isArray(detail?.user?.authorities) ? detail.user.authorities : []),
    ...(Array.isArray(practitioner?.user?.authorities) ? practitioner.user.authorities : []),
  ]
  return [...new Set([...serviceRoles, ...authorityRoles].map(trimValue).filter(Boolean))]
}

function buildEntityMap(items = []) {
  const map = new Map()
  items.forEach((item) => {
    if (!item) return
    ;[item.id, item.uid, item.userId, item.slug].map(normalizeId).filter(Boolean).forEach((id) => {
      if (!map.has(id)) map.set(id, item)
    })
  })
  return map
}

function getRoomBrandId(room, assignmentByRoomId) {
  return (
    normalizeId(room?.clinicBrandId) ||
    normalizeId(room?.clinicBrand?.id) ||
    normalizeId(room?.clinicBrandData?.id) ||
    normalizeId(assignmentByRoomId.get(normalizeId(room?.id))?.clinicBrandId)
  )
}

function getRoomLocationId(room, assignmentByRoomId) {
  return (
    normalizeId(room?.clinicLocationId) ||
    normalizeId(room?.clinicId) ||
    normalizeId(room?.clinicLocation?.id) ||
    normalizeId(room?.clinicLocationData?.id) ||
    normalizeId(assignmentByRoomId.get(normalizeId(room?.id))?.clinicLocationId)
  )
}

function getRoomTitle(room, fallbackId) {
  return displayTitle(room, fallbackId || 'Chamber room')
}

function getBrandTitle(room, brand, fallbackId) {
  return (
    room?.clinicBrandName ||
    room?.clinicBrandTitle ||
    room?.clinicBrand?.title ||
    room?.clinicBrand?.name ||
    displayTitle(brand, fallbackId || 'Clinic brand')
  )
}

function getLocationTitle(room, location, fallbackId) {
  return (
    room?.clinicLocationName ||
    room?.clinicName ||
    room?.clinicLocationTitle ||
    room?.clinicLocation?.title ||
    room?.clinicLocation?.name ||
    displayTitle(location, fallbackId || 'Clinic location')
  )
}

function formatAccessType(value) {
  const normalized = trimValue(value || 'SUPERVISOR').toUpperCase()
  if (normalized === 'LEARNER') return 'Learner'
  if (normalized === 'SUPERVISOR') return 'Supervisor'
  return normalized
    .toLowerCase()
    .split(/[-_\s]+/)
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join(' ')
}

function formatDateTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function collectDetailRooms(detail) {
  const rooms = []
  ;(detail?.clinicAssignments || []).forEach((assignment) => {
    ;(assignment?.chamberRooms || []).forEach((room) => {
      if (!room?.id) return
      rooms.push({
        ...room,
        clinicBrandId: room.clinicBrandId || assignment.clinicBrandId,
        clinicLocationId: room.clinicLocationId || assignment.clinicLocationId,
      })
    })
  })
  return rooms
}

function buildAssignmentByRoomId(detail) {
  const map = new Map()
  ;(detail?.clinicAssignments || []).forEach((assignment) => {
    ;(assignment?.chamberRooms || []).forEach((room) => {
      if (room?.id) map.set(room.id, assignment)
    })
  })
  return map
}

function buildRoomMap(bootstrap, detail, roomsWithAuditAccess) {
  const map = new Map()
  ;[
    ...(bootstrap?.chamberRooms || []),
    ...collectDetailRooms(detail),
    ...(roomsWithAuditAccess || []),
  ].forEach((room) => {
    if (room?.id) {
      map.set(room.id, { ...(map.get(room.id) || {}), ...room })
    }
  })
  return map
}

function finalizeGroupedRooms(groupsMap) {
  return Array.from(groupsMap.values()).map((brand) => ({
    ...brand,
    locations: Array.from(brand.locations.values()).map((location) => ({
      ...location,
      rooms: Array.from(location.rooms.values()),
    })),
  }))
}

function createRoomContext({ roomId, room, assignmentByRoomId, brandMap, locationMap }) {
  const brandId = getRoomBrandId(room, assignmentByRoomId)
  const locationId = getRoomLocationId(room, assignmentByRoomId)
  const brand = brandMap.get(brandId)
  const location = locationMap.get(locationId)
  return {
    brandId,
    locationId,
    roomId,
    brandTitle: getBrandTitle(room, brand, brandId),
    locationTitle: getLocationTitle(room, location, locationId),
    roomTitle: getRoomTitle(room, roomId),
  }
}

function getRoomEventTypes(room) {
  return [
    ...normalizeList(room?.eventTypesForThisRoom),
    ...normalizeList(room?.primaryEventTypesForThisRoom),
    ...normalizeList(room?.otherEventTypesForThisRoom),
    ...normalizeList(room?.supportedEventTypes),
    ...normalizeList(room?.eventTypes),
  ].map((eventType) => eventType.toLowerCase())
}

function roomSupportsEventType(room, eventType) {
  return getRoomEventTypes(room).includes(trimValue(eventType).toLowerCase())
}

function practitionerHasRole(detail, practitioner, role) {
  return getPractitionerRoleIds(detail, practitioner).includes(role)
}

function canManageAuditRoom({ userRoles, detail, practitioner, practitionerType, room }) {
  if (!Array.isArray(userRoles) || userRoles.length === 0) return false
  if (userRoles.includes(ROLE_ADMIN) || userRoles.includes(ROLE_AUDIT_ADMIN)) return true
  if (
    practitionerType === 'therapist' &&
    userRoles.includes(ROLE_THERAPIST_AUDIT_ADMIN) &&
    practitionerHasRole(detail, practitioner, ROLE_THERAPIST) &&
    roomSupportsEventType(room, EVENT_TYPE_THERAPY_SESSION)
  ) {
    return true
  }
  if (
    practitionerType === 'screener' &&
    userRoles.includes(ROLE_SCREENER_AUDIT_ADMIN) &&
    practitionerHasRole(detail, practitioner, ROLE_SCREENER) &&
    roomSupportsEventType(room, EVENT_TYPE_SCREENING)
  ) {
    return true
  }
  return false
}

function buildIncomingAuditorRows({
  practitionerId,
  bootstrap,
  detail,
  roomsWithAuditAccess,
}) {
  const rowsByRoomId = new Map()
  const practitionerMap = buildEntityMap([
    ...(bootstrap?.practitioners || []),
    detail?.practitioner,
  ])
  const brandMap = buildEntityMap(bootstrap?.clinicBrands || [])
  const locationMap = buildEntityMap(bootstrap?.clinicLocations || [])
  const assignmentByRoomId = buildAssignmentByRoomId(detail)
  const roomMap = buildRoomMap(bootstrap, detail, roomsWithAuditAccess)

  function ensureRow(roomId, room = null) {
    if (!roomId) return null
    const resolvedRoom = room || roomMap.get(roomId) || { id: roomId, title: roomId }
    const roomContext = createRoomContext({
      roomId,
      room: resolvedRoom,
      assignmentByRoomId,
      brandMap,
      locationMap,
    })
    if (!rowsByRoomId.has(roomId)) {
      rowsByRoomId.set(roomId, {
        ...roomContext,
        id: roomId,
        room: resolvedRoom,
        auditors: [],
        temporaryAuditors: [],
      })
    }
    return rowsByRoomId.get(roomId)
  }

  function addPermanentAuditor(row, auditorId, type = 'SUPERVISOR') {
    if (!row || !auditorId || row.auditors.some((auditor) => auditor.id === auditorId)) return
    const auditor = practitionerMap.get(auditorId)
    row.auditors.push({
      id: auditorId,
      name: displayTitle(auditor, auditorId),
      type: 'Permanent',
      accessType: formatAccessType(type),
      rawType: trimValue(type || 'SUPERVISOR').toUpperCase(),
      expiresOn: '',
    })
  }

  function addTemporaryAuditor(row, auditorId, type = 'SUPERVISOR', expiresOn = '') {
    if (!row || !auditorId) return
    const key = `${auditorId}:${type}:${expiresOn}`
    if (row.temporaryAuditors.some((auditor) => `${auditor.id}:${auditor.rawType}:${auditor.expiresOn}` === key)) return
    const auditor = practitionerMap.get(auditorId)
    row.temporaryAuditors.push({
      id: auditorId,
      name: displayTitle(auditor, auditorId),
      type: 'Temporary',
      accessType: formatAccessType(type),
      rawType: trimValue(type || 'SUPERVISOR').toUpperCase(),
      expiresOn,
    })
  }

  collectDetailRooms(detail).forEach((room) => {
    ensureRow(normalizeId(room?.id), room)
  })

  ;(roomsWithAuditAccess || []).forEach((room) => {
    const roomId = normalizeId(room?.id)
    const row = ensureRow(roomId, room)
    ;(room?.mentorsAndAuditors || []).forEach((combo) => {
      if (normalizeId(combo?.mentorId) !== practitionerId) return
      normalizeList(combo?.auditorIds).forEach((auditorId) => {
        addPermanentAuditor(row, auditorId, combo?.type || 'SUPERVISOR')
      })
      ;(combo?.auditAccessList || []).forEach((access) => {
        const auditorId = normalizeId(access?.auditorMentorId || access?.auditorPractitionerId)
        addTemporaryAuditor(
          row,
          auditorId,
          access?.type || combo?.type || 'SUPERVISOR',
          access?.expiresOn || access?.approvalExpiry || '',
        )
      })
    })
  })

  ;(detail?.incomingAuditors || []).forEach((entry) => {
    const roomId = normalizeId(entry?.chamberRoomId)
    const auditorId = normalizeId(entry?.auditorPractitionerId || entry?.auditorMentorId)
    if (!roomId || !auditorId) return
    const row = ensureRow(roomId)
    addPermanentAuditor(row, auditorId, entry?.type)
  })

  return Array.from(rowsByRoomId.values()).map((row) => ({
    ...row,
    auditors: row.auditors.sort((left, right) => left.name.localeCompare(right.name)),
    temporaryAuditors: row.temporaryAuditors.sort((left, right) => left.name.localeCompare(right.name)),
  }))
}

function buildIncomingAuditorGroups({ rows = [], includeEmpty = false, canShowEmptyRoom = () => false }) {
  const groupsMap = new Map()
  rows.forEach((row) => {
    const hasAuditors = row.auditors.length > 0 || row.temporaryAuditors.length > 0
    if (!hasAuditors && !(includeEmpty && canShowEmptyRoom(row))) return
    const brandKey = row.brandId || 'unknown-brand'
    const locationKey = row.locationId || 'unknown-location'
    const roomKey = row.roomId || 'unknown-room'

    if (!groupsMap.has(brandKey)) {
      groupsMap.set(brandKey, {
        id: brandKey,
        title: row.brandTitle,
        locations: new Map(),
      })
    }
    const brand = groupsMap.get(brandKey)
    if (!brand.locations.has(locationKey)) {
      brand.locations.set(locationKey, {
        id: locationKey,
        title: row.locationTitle,
        rooms: new Map(),
      })
    }
    const location = brand.locations.get(locationKey)
    if (!location.rooms.has(roomKey)) {
      location.rooms.set(roomKey, row)
    }
  })

  return finalizeGroupedRooms(groupsMap)
}

function buildAuditorDraftsByRoom(rows = []) {
  return rows.reduce((drafts, row) => {
    drafts[row.roomId] = row.auditors.map((auditor) => ({
      auditorPractitionerId: auditor.id,
      type: auditor.rawType || 'SUPERVISOR',
    }))
    return drafts
  }, {})
}

function normalizeDraftAuditors(auditors = []) {
  return auditors
    .map((auditor) => ({
      auditorPractitionerId: normalizeId(auditor?.auditorPractitionerId),
      type: trimValue(auditor?.type || 'SUPERVISOR').toUpperCase(),
    }))
    .filter((auditor) => auditor.auditorPractitionerId)
}

function draftMatchesRow(row, draftAuditors = []) {
  const rowKey = normalizeDraftAuditors(
    row.auditors.map((auditor) => ({
      auditorPractitionerId: auditor.id,
      type: auditor.rawType,
    })),
  )
    .map((auditor) => `${auditor.auditorPractitionerId}:${auditor.type}`)
    .sort()
    .join('|')
  const draftKey = normalizeDraftAuditors(draftAuditors)
    .map((auditor) => `${auditor.auditorPractitionerId}:${auditor.type}`)
    .sort()
    .join('|')
  return rowKey === draftKey
}

function buildOutgoingAuditCards({
  practitionerId,
  bootstrap,
  detail,
  roomsWithAuditAccess,
}) {
  const practitionerMap = buildEntityMap([
    ...(bootstrap?.practitioners || []),
    detail?.practitioner,
  ])
  const brandMap = buildEntityMap(bootstrap?.clinicBrands || [])
  const locationMap = buildEntityMap(bootstrap?.clinicLocations || [])
  const assignmentByRoomId = buildAssignmentByRoomId(detail)
  const roomMap = buildRoomMap(bootstrap, detail, roomsWithAuditAccess)
  const cardsMap = new Map()

  function addPermission(mentorToAuditId, roomContext, meta = {}) {
    if (!mentorToAuditId) return
    const mentor = practitionerMap.get(mentorToAuditId)
    if (!cardsMap.has(mentorToAuditId)) {
      cardsMap.set(mentorToAuditId, {
        id: mentorToAuditId,
        name: displayTitle(mentor, mentorToAuditId),
        subtitle: mentor?.subtitle || mentor?.type || '',
        slug: mentor?.slug || '',
        rooms: [],
      })
    }
    cardsMap.get(mentorToAuditId).rooms.push({
      ...roomContext,
      accessType: formatAccessType(meta.accessType),
      type: meta.type || 'Permanent',
      expiresOn: meta.expiresOn || '',
    })
  }

  ;(roomsWithAuditAccess || []).forEach((room) => {
    const roomId = normalizeId(room?.id)
    const roomContext = createRoomContext({
      roomId,
      room,
      assignmentByRoomId,
      brandMap,
      locationMap,
    })
    ;(room?.mentorsAndAuditors || []).forEach((combo) => {
      const mentorToAuditId = normalizeId(combo?.mentorId)
      if (!mentorToAuditId || mentorToAuditId === practitionerId) return
      if (normalizeList(combo?.auditorIds).includes(practitionerId)) {
        addPermission(mentorToAuditId, roomContext, {
          accessType: combo?.type || 'SUPERVISOR',
          type: 'Permanent',
        })
      }
      ;(combo?.auditAccessList || []).forEach((access) => {
        const auditorId = normalizeId(access?.auditorMentorId || access?.auditorPractitionerId)
        if (auditorId !== practitionerId) return
        addPermission(mentorToAuditId, roomContext, {
          accessType: access?.type || combo?.type || 'SUPERVISOR',
          type: 'Temporary',
          expiresOn: access?.expiresOn || access?.approvalExpiry || '',
        })
      })
    })
  })

  const rawAssignments = [
    ...(detail?.auditorAssignments || []),
    ...(detail?.auditPermissions || []),
    ...(detail?.auditingPermissions || []),
  ]
  rawAssignments.forEach((assignment) => {
    const mentorToAuditId = normalizeId(
      assignment?.mentorId || assignment?.mentorToAuditId || assignment?.practitionerId,
    )
    const roomIds = normalizeList(assignment?.chamberRoomIds)
    if (roomIds.length === 0) {
      addPermission(
        mentorToAuditId,
        {
          brandId: 'all-brands',
          locationId: 'all-locations',
          roomId: 'all-rooms',
          brandTitle: 'All configured clinic brands',
          locationTitle: 'All configured locations',
          roomTitle: 'All rooms',
        },
        { accessType: assignment?.type, type: 'Permanent' },
      )
      return
    }
    roomIds.forEach((roomId) => {
      const room = roomMap.get(roomId) || { id: roomId, title: roomId }
      addPermission(
        mentorToAuditId,
        createRoomContext({
          roomId,
          room,
          assignmentByRoomId,
          brandMap,
          locationMap,
        }),
        { accessType: assignment?.type, type: 'Permanent' },
      )
    })
  })

  return Array.from(cardsMap.values()).map((card) => ({
    ...card,
    rooms: card.rooms.filter((room, index, rooms) => {
      const key = `${room.roomId}:${room.type}:${room.expiresOn}`
      return rooms.findIndex((candidate) => `${candidate.roomId}:${candidate.type}:${candidate.expiresOn}` === key) === index
    }),
  }))
}

export default function AuditPage() {
  const navigate = useNavigate()
  const location = useLocation()

  const query = useMemo(() => new URLSearchParams(location.search), [location.search])
  const selectedSection = query.get('section') || ''
  const scopedPractitionerId =
    normalizeId(query.get('practitionerId')) ||
    normalizeList(query.getAll('auditorMentorIds'))[0] ||
    ''
  const scopedPractitionerSlug = normalizeId(query.get('practitionerSlug'))
  const hasScopedPractitioner = Boolean(scopedPractitionerId || scopedPractitionerSlug)

  const [contextState, setContextState] = useState({
    loading: false,
    error: '',
    bootstrap: null,
    detail: null,
    roomsWithAuditAccess: [],
  })
  const [auditorsEditMode, setAuditorsEditMode] = useState(false)
  const [auditorDraftsByRoom, setAuditorDraftsByRoom] = useState({})
  const [savingAuditorRoomId, setSavingAuditorRoomId] = useState('')
  const userRoles = useMemo(() => getUserRoles() || [], [])

  const loadPractitionerAuditContext = useCallback(async () => {
    if (!hasScopedPractitioner) return

    setContextState((prev) => ({ ...prev, loading: true, error: '' }))
    try {
      const [bootstrapResponse, detailResponse, roomsResponse] = await Promise.all([
        getPractitionerAuditBootstrap(),
        getPractitionerAuditDetail({
          practitionerId: scopedPractitionerId,
          practitionerSlug: scopedPractitionerSlug,
        }),
        getPractitionerAuditRooms({
          practitionerId: scopedPractitionerId,
          practitionerSlug: scopedPractitionerSlug,
        }),
      ])

      setContextState({
        loading: false,
        error: detailResponse.ok && roomsResponse.ok
          ? ''
          : detailResponse.message || roomsResponse.message || 'Unable to load practitioner audit profile.',
        bootstrap: bootstrapResponse.ok ? bootstrapResponse.data : null,
        detail: detailResponse.ok ? detailResponse.data : null,
        roomsWithAuditAccess: roomsResponse.ok ? roomsResponse.data : [],
      })
    } catch (error) {
      console.error('Error loading practitioner audit context:', error)
      setContextState({
        loading: false,
        error: 'Unable to load practitioner audit profile.',
        bootstrap: null,
        detail: null,
        roomsWithAuditAccess: [],
      })
    }
  }, [hasScopedPractitioner, scopedPractitionerId, scopedPractitionerSlug])

  useEffect(() => {
    loadPractitionerAuditContext()
  }, [loadPractitionerAuditContext])

  const practitioner = useMemo(() => {
    const fromDetail = contextState.detail?.practitioner
    if (fromDetail?.id || fromDetail?.slug) return fromDetail
    const practitionerMap = buildEntityMap(contextState.bootstrap?.practitioners || [])
    return practitionerMap.get(scopedPractitionerId) || practitionerMap.get(scopedPractitionerSlug) || null
  }, [contextState.bootstrap, contextState.detail, scopedPractitionerId, scopedPractitionerSlug])

  const practitionerType = getPractitionerType(contextState.detail, practitioner)
  const practitionerId = normalizeId(practitioner?.id || scopedPractitionerId)
  const practitionerName = displayTitle(practitioner, scopedPractitionerId || scopedPractitionerSlug || 'Practitioner')
  const auditorRoomRows = useMemo(
    () =>
      practitionerId
        ? buildIncomingAuditorRows({
            practitionerId,
            bootstrap: contextState.bootstrap,
            detail: contextState.detail,
            roomsWithAuditAccess: contextState.roomsWithAuditAccess,
          })
        : [],
    [contextState.bootstrap, contextState.detail, contextState.roomsWithAuditAccess, practitionerId],
  )
  const canManageAuditorRow = useCallback(
    (row) =>
      canManageAuditRoom({
        userRoles,
        detail: contextState.detail,
        practitioner,
        practitionerType,
        room: row?.room,
      }),
    [contextState.detail, practitioner, practitionerType, userRoles],
  )
  const incomingGroups = useMemo(
    () =>
      buildIncomingAuditorGroups({
        rows: auditorRoomRows,
        includeEmpty: auditorsEditMode,
        canShowEmptyRoom: canManageAuditorRow,
      }),
    [auditorRoomRows, auditorsEditMode, canManageAuditorRow],
  )
  const canManageAnyAuditorRoom = useMemo(
    () => auditorRoomRows.some((row) => canManageAuditorRow(row)),
    [auditorRoomRows, canManageAuditorRow],
  )
  const outgoingCards = useMemo(
    () =>
      practitionerId
        ? buildOutgoingAuditCards({
            practitionerId,
            bootstrap: contextState.bootstrap,
            detail: contextState.detail,
            roomsWithAuditAccess: contextState.roomsWithAuditAccess,
          })
        : [],
    [contextState.bootstrap, contextState.detail, contextState.roomsWithAuditAccess, practitionerId],
  )

  useEffect(() => {
    if (!auditorsEditMode) return
    setAuditorDraftsByRoom(buildAuditorDraftsByRoom(auditorRoomRows))
  }, [auditorRoomRows, auditorsEditMode])

  function enterAuditorsEditMode() {
    setAuditorDraftsByRoom(buildAuditorDraftsByRoom(auditorRoomRows))
    setAuditorsEditMode(true)
  }

  function exitAuditorsEditMode() {
    setAuditorsEditMode(false)
    setAuditorDraftsByRoom({})
    setSavingAuditorRoomId('')
  }

  function setRoomDraft(roomId, updater) {
    setAuditorDraftsByRoom((prev) => {
      const current = normalizeDraftAuditors(prev[roomId] || [])
      return {
        ...prev,
        [roomId]: normalizeDraftAuditors(typeof updater === 'function' ? updater(current) : updater),
      }
    })
  }

  function addAuditorToRoomDraft(roomId, practitionerOption) {
    const auditorPractitionerId = normalizeId(practitionerOption?.id)
    if (!auditorPractitionerId) return
    setRoomDraft(roomId, (current) => {
      if (current.some((auditor) => auditor.auditorPractitionerId === auditorPractitionerId)) {
        return current
      }
      return [...current, { auditorPractitionerId, type: 'SUPERVISOR' }]
    })
  }

  function updateAuditorTypeForRoom(roomId, auditorPractitionerId, type) {
    setRoomDraft(roomId, (current) =>
      current.map((auditor) =>
        auditor.auditorPractitionerId === auditorPractitionerId
          ? { ...auditor, type: trimValue(type || 'SUPERVISOR').toUpperCase() }
          : auditor,
      ),
    )
  }

  function removeAuditorFromRoomDraft(roomId, auditorPractitionerId) {
    setRoomDraft(roomId, (current) =>
      current.filter((auditor) => auditor.auditorPractitionerId !== auditorPractitionerId),
    )
  }

  function resetRoomDraft(row) {
    setRoomDraft(
      row.roomId,
      row.auditors.map((auditor) => ({
        auditorPractitionerId: auditor.id,
        type: auditor.rawType || 'SUPERVISOR',
      })),
    )
  }

  async function saveRoomAuditors(row) {
    if (!canManageAuditorRow(row)) {
      toast.error('Your audit role cannot manage this chamber room.')
      return
    }
    const draftAuditors = normalizeDraftAuditors(auditorDraftsByRoom[row.roomId] || [])
    setSavingAuditorRoomId(row.roomId)
    try {
      const response = await savePractitionerRoomAuditors({
        practitionerId,
        practitionerSlug: practitioner?.slug || scopedPractitionerSlug,
        chamberRoomId: row.roomId,
        auditors: draftAuditors.map((auditor) => ({
          chamberRoomId: row.roomId,
          auditorPractitionerId: auditor.auditorPractitionerId,
          type: auditor.type,
        })),
      })
      if (!response.ok || !response.data) {
        toast.error(response.message || 'Unable to save chamber-room auditors.')
        return
      }
      setContextState((prev) => ({
        ...prev,
        detail: response.data,
      }))
      toast.success('Room auditors saved.')
    } catch (error) {
      console.error('Error saving room auditors:', error)
      toast.error('Unable to save chamber-room auditors.')
    } finally {
      setSavingAuditorRoomId('')
    }
  }

  function updateSection(nextSection) {
    const params = new URLSearchParams(location.search)
    if (nextSection) params.set('section', nextSection)
    else params.delete('section')
    navigate({
      pathname: location.pathname,
      search: params.toString() ? `?${params.toString()}` : '',
    })
  }

  function buildOnboardingUrl(section) {
    const params = new URLSearchParams()
    if (practitionerType) params.set('type', practitionerType)
    if (practitionerId) params.set('practitioner-id', practitionerId)
    if (practitioner?.slug) params.set('practitioner-slug', practitioner.slug)
    if (section) params.set('section', section)
    const basePath =
      section === 'auditor-access'
        ? '/support-home/practitioner-management/onboarding/auditor'
        : '/support-home/practitioner-management/onboarding'
    return `${basePath}?${params.toString()}`
  }

  const actions = [
    {
      id: 'audit-requests',
      title: 'Audit Requests',
      description: hasScopedPractitioner
        ? 'Requests this practitioner raised to audit other chamber rooms.'
        : 'Review and approve chamber-room audit requests.',
      icon: <Assignment />,
      countLabel: null,
    },
    {
      id: 'auditors',
      title: 'Auditors',
      description: 'People who can audit this practitioner, grouped by clinic, location, and room.',
      icon: <ManageAccounts />,
      countLabel: hasScopedPractitioner ? `${incomingGroups.length} clinic group${incomingGroups.length === 1 ? '' : 's'}` : null,
    },
    {
      id: 'auditing-permissions',
      title: 'Auditing Permissions',
      description: 'Other practitioners this person can audit across chamber rooms.',
      icon: <FactCheck />,
      countLabel: hasScopedPractitioner ? `${outgoingCards.length} practitioner${outgoingCards.length === 1 ? '' : 's'}` : null,
    },
  ]

  function renderScopedHeader() {
    if (!hasScopedPractitioner) return null
    return (
      <SectionPaper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44 }}>
              <Person />
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>
                {practitionerName}
              </Typography>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                <Chip size="small" label={practitionerType || 'practitioner'} variant="outlined" />
                {practitionerId && <Chip size="small" label={practitionerId} variant="outlined" />}
              </Stack>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button
              size="small"
              variant="outlined"
              startIcon={<Refresh />}
              onClick={loadPractitionerAuditContext}
              disabled={contextState.loading}
            >
              Refresh
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<Edit />}
              onClick={() => navigate(buildOnboardingUrl('auditor-access'))}
              disabled={!practitionerId}
            >
              Edit Audit Access
            </Button>
          </Stack>
        </Stack>
      </SectionPaper>
    )
  }

  function renderActionTiles() {
    return (
      <Grid container spacing={2}>
        {actions.map((action) => (
          <Grid item xs={12} md={4} key={action.id}>
            <StyledCard selected={selectedSection === action.id} onClick={() => updateSection(action.id)}>
              <CardContent sx={{ p: 2.25 }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <TileIconContainer color={action.id === 'audit-requests' ? 'primary.main' : 'secondary'}>
                      {React.cloneElement(action.icon, { fontSize: 'small' })}
                    </TileIconContainer>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {action.title}
                      </Typography>
                      {action.countLabel && (
                        <Typography variant="caption" color="text.secondary">
                          {action.countLabel}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {action.description}
                  </Typography>
                </Stack>
              </CardContent>
            </StyledCard>
          </Grid>
        ))}
      </Grid>
    )
  }

  function renderAuditorsSection() {
    if (!hasScopedPractitioner) {
      return (
        <Alert severity="info">
          Pick this from a practitioner card to see who audits that practitioner.
        </Alert>
      )
    }
    if (contextState.loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      )
    }
    const practitionerOptions = contextState.bootstrap?.practitioners || []
    return (
      <SectionPaper sx={{ p: 2.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Auditors For {practitionerName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Grouped as clinic brand, clinic location, and chamber room.
            </Typography>
          </Box>
          {canManageAnyAuditorRoom && (
            <Stack direction="row" spacing={1}>
              {auditorsEditMode && (
                <Button variant="outlined" startIcon={<Close />} onClick={exitAuditorsEditMode}>
                  Done
                </Button>
              )}
              <Button
                variant={auditorsEditMode ? 'outlined' : 'contained'}
                startIcon={<Edit />}
                onClick={auditorsEditMode ? () => setAuditorDraftsByRoom(buildAuditorDraftsByRoom(auditorRoomRows)) : enterAuditorsEditMode}
                disabled={!practitionerId}
              >
                {auditorsEditMode ? 'Reset Drafts' : 'Manage Per-Room Auditors'}
              </Button>
            </Stack>
          )}
        </Stack>

        {incomingGroups.length === 0 ? (
          <EmptyPanel
            icon={<Security sx={{ fontSize: 44 }} />}
            title={auditorsEditMode ? 'No rooms in your audit scope' : 'No auditors configured'}
            body={
              auditorsEditMode
                ? 'There are no chamber rooms available for your audit role scope on this practitioner.'
                : 'This practitioner does not currently have room-level auditors in the loaded clinic-room access data.'
            }
            action={
              canManageAnyAuditorRoom && !auditorsEditMode ? (
                <Button variant="outlined" startIcon={<Edit />} onClick={enterAuditorsEditMode}>
                  Manage Per-Room Auditors
                </Button>
              ) : null
            }
          />
        ) : (
          <Stack spacing={2}>
            {incomingGroups.map((brand) => (
              <Paper key={brand.id} variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <Business color="primary" fontSize="small" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {brand.title}
                  </Typography>
                </Stack>
                <Stack spacing={1.5}>
                  {brand.locations.map((clinicLocation) => (
                    <Box key={`${brand.id}-${clinicLocation.id}`}>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                        <LocationOn color="action" fontSize="small" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {clinicLocation.title}
                        </Typography>
                      </Stack>
                      <Stack spacing={1}>
                        {clinicLocation.rooms.map((room) => {
                            const roomCanManage = canManageAuditorRow(room)
                            const draftAuditors = normalizeDraftAuditors(auditorDraftsByRoom[room.roomId] || [])
                            const draftIds = new Set(draftAuditors.map((auditor) => auditor.auditorPractitionerId))
                            const candidateOptions = practitionerOptions.filter(
                              (option) =>
                                option?.id &&
                                option.id !== practitionerId &&
                                !draftIds.has(option.id),
                            )
                            const hasAuditors = room.auditors.length > 0 || room.temporaryAuditors.length > 0
                            const dirty = !draftMatchesRow(room, draftAuditors)
                            return (
                              <Paper
                                key={`${clinicLocation.id}-${room.roomId}`}
                                variant="outlined"
                                sx={{ p: 1.5, borderRadius: 1, bgcolor: hasAuditors ? 'rgba(248, 250, 252, 0.9)' : 'rgba(255, 251, 235, 0.75)' }}
                              >
                                <Stack spacing={1.25}>
                                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between">
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <MeetingRoom fontSize="small" color="action" />
                                      <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                          {room.roomTitle}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {room.roomId}
                                        </Typography>
                                      </Box>
                                    </Stack>
                                    <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" justifyContent={{ md: 'flex-end' }}>
                                      {!hasAuditors && auditorsEditMode && (
                                        <Chip size="small" color="warning" variant="outlined" label="No auditors" />
                                      )}
                                      {!roomCanManage && auditorsEditMode && (
                                        <Chip size="small" variant="outlined" label="Outside your audit scope" />
                                      )}
                                      {!auditorsEditMode && room.auditors.map((auditor) => (
                                        <Chip
                                          key={`${room.roomId}-${auditor.id}-${auditor.type}-${auditor.expiresOn}`}
                                          size="small"
                                          color="primary"
                                          label={`${auditor.name} - ${auditor.accessType}`}
                                        />
                                      ))}
                                      {!auditorsEditMode && room.temporaryAuditors.map((auditor) => (
                                        <Chip
                                          key={`${room.roomId}-${auditor.id}-${auditor.type}-${auditor.expiresOn}`}
                                          size="small"
                                          color="warning"
                                          variant="outlined"
                                          label={`${auditor.name} - ${auditor.accessType}${auditor.expiresOn ? ` - expires ${formatDateTime(auditor.expiresOn)}` : ''}`}
                                        />
                                      ))}
                                    </Stack>
                                  </Stack>

                                  {auditorsEditMode && (
                                    <Stack spacing={1.25}>
                                      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                        {draftAuditors.length === 0 ? (
                                          <Typography variant="caption" color="text.secondary">
                                            No permanent auditors assigned for this room.
                                          </Typography>
                                        ) : (
                                          draftAuditors.map((draftAuditor) => {
                                            const auditor = practitionerOptions.find((option) => option.id === draftAuditor.auditorPractitionerId)
                                            return (
                                              <Stack
                                                key={`${room.roomId}-${draftAuditor.auditorPractitionerId}`}
                                                direction={{ xs: 'column', sm: 'row' }}
                                                spacing={0.75}
                                                alignItems={{ sm: 'center' }}
                                                sx={{
                                                  border: '1px solid #dbe2ea',
                                                  borderRadius: 1,
                                                  px: 1,
                                                  py: 0.75,
                                                  bgcolor: 'background.paper',
                                                }}
                                              >
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                  {displayTitle(auditor, draftAuditor.auditorPractitionerId)}
                                                </Typography>
                                                <TextField
                                                  select
                                                  size="small"
                                                  value={draftAuditor.type}
                                                  onChange={(event) =>
                                                    updateAuditorTypeForRoom(
                                                      room.roomId,
                                                      draftAuditor.auditorPractitionerId,
                                                      event.target.value,
                                                    )
                                                  }
                                                  disabled={!roomCanManage || savingAuditorRoomId === room.roomId}
                                                  sx={{ minWidth: 132 }}
                                                >
                                                  {AUDITOR_TYPE_OPTIONS.map((option) => (
                                                    <MenuItem key={option.id} value={option.id}>
                                                      {option.label}
                                                    </MenuItem>
                                                  ))}
                                                </TextField>
                                                <Button
                                                  size="small"
                                                  color="error"
                                                  onClick={() =>
                                                    removeAuditorFromRoomDraft(
                                                      room.roomId,
                                                      draftAuditor.auditorPractitionerId,
                                                    )
                                                  }
                                                  disabled={!roomCanManage || savingAuditorRoomId === room.roomId}
                                                >
                                                  Remove
                                                </Button>
                                              </Stack>
                                            )
                                          })
                                        )}
                                      </Stack>

                                      {room.temporaryAuditors.length > 0 && (
                                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                                          {room.temporaryAuditors.map((auditor) => (
                                            <Chip
                                              key={`${room.roomId}-temp-${auditor.id}-${auditor.expiresOn}`}
                                              size="small"
                                              color="warning"
                                              variant="outlined"
                                              label={`${auditor.name} - request access${auditor.expiresOn ? ` - expires ${formatDateTime(auditor.expiresOn)}` : ''}`}
                                            />
                                          ))}
                                        </Stack>
                                      )}

                                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                                        <Autocomplete
                                          size="small"
                                          fullWidth
                                          options={candidateOptions}
                                          value={null}
                                          disabled={!roomCanManage || savingAuditorRoomId === room.roomId}
                                          onChange={(_event, value) => addAuditorToRoomDraft(room.roomId, value)}
                                          getOptionLabel={(option) =>
                                            option?.subtitle
                                              ? `${displayTitle(option, option.id)} (${option.subtitle})`
                                              : displayTitle(option, option?.id || '')
                                          }
                                          isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                          renderInput={(params) => (
                                            <TextField
                                              {...params}
                                              label="Add auditor"
                                              placeholder="Search practitioner"
                                            />
                                          )}
                                        />
                                        <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-end', md: 'initial' }}>
                                          <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => resetRoomDraft(room)}
                                            disabled={!dirty || savingAuditorRoomId === room.roomId}
                                          >
                                            Reset
                                          </Button>
                                          <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={savingAuditorRoomId === room.roomId ? <CircularProgress size={16} color="inherit" /> : <Save />}
                                            onClick={() => saveRoomAuditors(room)}
                                            disabled={!roomCanManage || !dirty || savingAuditorRoomId === room.roomId}
                                          >
                                            Save Room
                                          </Button>
                                        </Stack>
                                      </Stack>
                                    </Stack>
                                  )}
                                </Stack>
                              </Paper>
                            )
                        })}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            ))}
          </Stack>
        )}
      </SectionPaper>
    )
  }

  function renderAuditingPermissionsSection() {
    if (!hasScopedPractitioner) {
      return (
        <Alert severity="info">
          Pick this from a practitioner card to see whom that practitioner can audit.
        </Alert>
      )
    }
    if (contextState.loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      )
    }
    return (
      <SectionPaper sx={{ p: 2.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Auditing Permissions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Practitioners {practitionerName} can audit, with clinic and room identity.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => navigate(buildOnboardingUrl('auditor-access'))}
            disabled={!practitionerId}
          >
            Manage Permissions
          </Button>
        </Stack>

        {outgoingCards.length === 0 ? (
          <EmptyPanel
            icon={<FactCheck sx={{ fontSize: 44 }} />}
            title="No auditing permissions configured"
            body="This practitioner is not currently listed as an auditor for other practitioners in the loaded room access data."
            action={
              <Button variant="outlined" startIcon={<OpenInNew />} onClick={() => navigate(buildOnboardingUrl('auditor-access'))}>
                Open Auditor Access Editor
              </Button>
            }
          />
        ) : (
          <Grid container spacing={2}>
            {outgoingCards.map((card) => (
              <Grid item xs={12} md={6} key={card.id}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, height: '100%' }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar sx={{ bgcolor: 'secondary.main', width: 40, height: 40 }}>
                        <Person />
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                          {card.name}
                        </Typography>
                        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                          {card.subtitle && <Chip size="small" label={card.subtitle} variant="outlined" />}
                          <Chip size="small" label={card.id} variant="outlined" />
                        </Stack>
                      </Box>
                    </Stack>
                    <Divider />
                    <Stack spacing={1}>
                      {card.rooms.map((room) => (
                        <Paper key={`${card.id}-${room.roomId}-${room.type}-${room.expiresOn}`} variant="outlined" sx={{ p: 1.25, borderRadius: 1, bgcolor: 'rgba(248, 250, 252, 0.9)' }}>
                          <Stack spacing={0.75}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {room.roomTitle}
                            </Typography>
                            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                              <Chip size="small" icon={<Business />} label={room.brandTitle} variant="outlined" />
                              <Chip size="small" icon={<LocationOn />} label={room.locationTitle} variant="outlined" />
                              <Chip
                                size="small"
                                color={room.type === 'Temporary' ? 'warning' : 'primary'}
                                variant={room.type === 'Temporary' ? 'outlined' : 'filled'}
                                label={`${room.accessType}${room.expiresOn ? ` - expires ${formatDateTime(room.expiresOn)}` : ''}`}
                              />
                            </Stack>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </SectionPaper>
    )
  }

  function renderSelectedSection() {
    if (!selectedSection) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          Choose one of the audit actions above.
        </Alert>
      )
    }
    if (selectedSection === 'audit-requests') {
      return (
        <SectionPaper sx={{ mt: 2, overflow: 'hidden' }}>
          <AuditRequestsListing isFromTherapists={hasScopedPractitioner} embedded />
        </SectionPaper>
      )
    }
    if (selectedSection === 'auditors') {
      return <Box sx={{ mt: 2 }}>{renderAuditorsSection()}</Box>
    }
    if (selectedSection === 'auditing-permissions') {
      return <Box sx={{ mt: 2 }}>{renderAuditingPermissionsSection()}</Box>
    }
    return null
  }

  return (
    <>
      <SupportHeader 
        heading="Audit Management" 
        showBackButton 
        backPath={hasScopedPractitioner ? '/support-home/practitioner-management/therapists' : '/support-home/practitioner-management'}
      />
      <Box
        sx={{
          width: '100%',
          padding: { xs: '16px', sm: '24px', md: '32px' },
          maxWidth: '1400px',
          margin: '0 auto',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          {selectedSection && (
            <Tooltip title="Back to audit actions">
              <IconButton size="small" onClick={() => updateSection('')}>
                <ArrowBack fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {hasScopedPractitioner ? 'Practitioner Audit' : 'Audit Management'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Requests, per-room auditors, and audit permissions in one place.
            </Typography>
          </Box>
        </Stack>

        {contextState.error && hasScopedPractitioner && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {contextState.error}
          </Alert>
        )}
        {renderScopedHeader()}
        {renderActionTiles()}
        {renderSelectedSection()}
      </Box>
    </>
  )
}
